/**
 * Paths to skip for all creative/asset uploads (OS cruft, authoring sources).
 * @param {string} rel - forward-slash path or filename
 */
export function isJunkUploadRelPath(rel) {
    const n = (rel || '').replace(/\\/g, '/');
    if (!n) return true;
    const lower = n.toLowerCase();
    if (lower.includes('__macosx')) return true;
    if (/(^|\/)desktop\.ini$/i.test(n)) return true;
    if (/\.(fla|psd)$/i.test(n)) return true;
    if (lower.endsWith('.ds_store') || lower.includes('/.ds_store')) return true;
    if (lower.endsWith('thumbs.db')) return true;
    if (n.split('/').some((seg) => /^\.[^./]+$/.test(seg))) return true;
    return false;
}

function normalizeUploadPath(p) {
    return (p || '').replace(/\\/g, '/').replace(/^\/+/, '');
}

/** True if this relative path is an actions.json click-map file. */
export function isActionsJsonRelPath(rel) {
    return /(^|\/)actions\.json$/i.test(normalizeUploadPath(rel));
}

function pathBasename(p) {
    const n = normalizeUploadPath(p);
    const i = n.lastIndexOf('/');
    return i >= 0 ? n.slice(i + 1) : n;
}

const MIME_BY_EXT = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    svg: 'image/svg+xml',
    webp: 'image/webp',
    mp4: 'video/mp4',
    webm: 'video/webm'
};

/** Basename without extension: `lower_layer.png` → `lower_layer`. */
export function assetStem(filename) {
    const base = pathBasename(filename || '');
    if (!base) return '';
    const i = base.lastIndexOf('.');
    return i > 0 ? base.slice(0, i) : base;
}

export function extensionFromFilename(filename) {
    const base = pathBasename(filename || '');
    const i = base.lastIndexOf('.');
    return i > 0 ? base.slice(i + 1).toLowerCase() : '';
}

export function mimeForExtension(ext, fallback) {
    return MIME_BY_EXT[String(ext || '').toLowerCase()] || fallback || '';
}

/**
 * Slot identity is `asset_name`. When present, a saved file matches by stem
 * (`lower_layer.svg` occupies the `lower_layer` slot). Without `asset_name`,
 * matching is exact filename only (layout assets, polite.jpg, main.mp4).
 */
export function matchesAssetSlot(savedFilename, slot = {}) {
    const saved = pathBasename(savedFilename || '');
    if (!saved) return false;
    if (slot.asset_name) {
        return assetStem(saved).toLowerCase() === String(slot.asset_name).toLowerCase();
    }
    if (slot.filename && saved === pathBasename(slot.filename)) {
        return true;
    }
    return false;
}

export function findAssetForSlot(assets, slot) {
    if (!Array.isArray(assets) || !slot) return null;
    return assets.find((a) => matchesAssetSlot(a?.filename, slot)) || null;
}

export function findAssetsForSlot(assets, slot) {
    if (!Array.isArray(assets) || !slot) return [];
    return assets.filter((a) => matchesAssetSlot(a?.filename, slot));
}

/** Keep the slot basename, take the extension from the uploaded file. */
export function slotFilenameFromUpload(slot, uploadedName) {
    if (slot?.asset_name) {
        const ext = extensionFromFilename(uploadedName);
        if (ext) return `${slot.asset_name}.${ext}`;
        const hintExt = extensionFromFilename(slot.filename);
        return hintExt ? `${slot.asset_name}.${hintExt}` : slot.asset_name;
    }
    return slot?.filename || uploadedName || '';
}

/**
 * Map an upload path to a stored creative asset path when the match is unambiguous.
 * Used for non-HTML replacements (e.g. flat `script.js` → existing `scripts/script.js`).
 * @param {string} uploadPath
 * @param {Array<{ filename?: string, path?: string }>} assets
 */
export function resolveUploadPathAgainstAssets(uploadPath, assets) {
    const norm = normalizeUploadPath(uploadPath);
    if (!norm || !Array.isArray(assets) || !assets.length) return norm;

    const existing = [];
    const seen = new Set();
    for (const a of assets) {
        const p = normalizeUploadPath(a?.filename || a?.path || '');
        if (!p || seen.has(p)) continue;
        seen.add(p);
        existing.push(p);
    }
    if (!existing.length) return norm;

    if (existing.includes(norm)) return norm;

    const suffixMatches = existing.filter((p) => p === norm || p.endsWith('/' + norm));
    if (suffixMatches.length === 1) return suffixMatches[0];

    const base = pathBasename(norm);
    if (!base) return norm;

    const baseMatches = existing.filter((p) => pathBasename(p) === base);
    if (baseMatches.length === 1) return baseMatches[0];

    return norm;
}

/**
 * When every file shares one top-level folder (typical folder drop), remove that prefix
 * so "MyAd/scripts/foo.js" becomes "scripts/foo.js".
 * @param {Array<File>} batch
 * @param {(file: File) => string} getRel
 */
export function stripCommonRootPrefixFromBatch(batch, getRel) {
    if (!Array.isArray(batch) || !batch.length) return;

    const paths = batch
        .map((f) => normalizeUploadPath(getRel(f)))
        .filter((p) => p && !isJunkUploadRelPath(p));
    if (!paths.length) return;

    const split = paths.map((p) => p.split('/'));
    if (!split.every((parts) => parts.length >= 2)) return;

    const root = split[0][0];
    if (!root || !split.every((parts) => parts[0] === root)) return;

    batch.forEach((file) => {
        const rel = normalizeUploadPath(getRel(file));
        if (!rel || isJunkUploadRelPath(rel)) return;
        const parts = rel.split('/');
        if (parts.length < 2 || parts[0] !== root) return;
        const stripped = parts.slice(1).join('/');
        file.fullPath = stripped;
        if (file.webkitRelativePath) {
            const wpParts = normalizeUploadPath(file.webkitRelativePath).split('/');
            if (wpParts.length >= 2 && wpParts[0] === root) {
                file.webkitRelativePath = wpParts.slice(1).join('/');
            }
        }
    });
}

const isImage = (filetype) => filetype.includes('image');

const isVideo = (filetype) => filetype.includes('video');

const analyzeFile = (file) => {
    return new Promise((resolve) => {
        const output = {};
        if (isImage(file.type)) {
            const img = new Image();
            img.onload = function() {
                window.URL.revokeObjectURL(img.src);
                output.width = Math.max(img.naturalWidth,img.width);
                output.height = Math.max(img.naturalHeight,img.height);
                resolve(output);
            }
            img.src = URL.createObjectURL(file);
    
        } else if(isVideo(file.type)) {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.onloadedmetadata = function() {
                window.URL.revokeObjectURL(video.src);
                output.duration = video.duration;
                output.width = Math.max(video.videoWidth,video.width);
                output.height = Math.max(video.videoHeight,video.height);
                resolve(output);
            }
            video.src = URL.createObjectURL(file);
        } else {
            resolve(output);
        }

    })
}

/** Used by bulk creative import — nested Promise.all(zip) can return arrays of ads. */
function flattenCreativePayloadList(input) {
    const out = [];
    function walk(x) {
        if (x == null) return;
        if (Array.isArray(x)) {
            x.forEach(walk);
            return;
        }
        if (
            typeof x === 'object'
            && Array.isArray(x.files)
            && (x.type === 'Banner' || x.type === 'VAST')
        ) {
            out.push(x);
        }
    }
    walk(input);
    return out;
}

export { analyzeFile, flattenCreativePayloadList };